import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Spinner, Badge, Tab, Tabs, Table, Modal } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUserClock,
    faCheckCircle,
    faPlay,
    faStop,
    faExclamationTriangle,
    faNotesMedical,
    faHistory,
    faClock,
    faSignOutAlt,
    faUserCircle,
    faUsers,
    faChartLine,
    faFileExcel,
    faTags,
    faTimesCircle
} from '@fortawesome/free-solid-svg-icons';
import request from '../../../../request';
import Notify from '../../../compartidos/Notify';
import './SpecialistConsole.css';

/**
 * SpecialistConsole - Premium Unified Attention Panel
 */
const SpecialistConsole = ({ ipserv, specialistId, onLogout }) => {
    const [loading, setLoading] = useState(false);
    const [turns, setTurns] = useState([]);
    const [history, setHistory] = useState([]);
    const [activeTurn, setActiveTurn] = useState(null);
    const [observations, setObservations] = useState('');
    
    // Specialist & Branch Data
    const [profile, setProfile] = useState(null);
    const [activeSpecialtyId, setActiveSpecialtyId] = useState(null);
    const [specialties, setSpecialties] = useState([]);
    
    // Timer & Clock
    const [timeLeft, setTimeLeft] = useState(0);
    const [currentTime, setCurrentTime] = useState(new Date());
    const timerRef = useRef(null);

    // Cancellation Modal State
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [selectedTurnForCancel, setSelectedTurnForCancel] = useState(null);
    const [cancelReason, setCancelReason] = useState('');

    const API_BASE = ipserv;

    // Live Clock
    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    const loadData = () => {
        if (!specialistId) return;
        setLoading(true);
        
        request.Get(API_BASE + 'turnero/specialist-profile', { specialistId }, r => {
            if (r.success) {
                setProfile(r.data);
                setSpecialties(r.specialties || []);
                if (r.specialties?.length > 0 && !activeSpecialtyId) {
                    setActiveSpecialtyId(r.specialties[0].EspecialidadId);
                }
            }
        });

        loadQueue();
        loadHistory();
    };

    const loadQueue = () => {
        const today = new Date().toISOString().split('T')[0];
        request.Get(API_BASE + 'specialist/turns', { 
            specialistId, 
            date: today,
            specialtyId: activeSpecialtyId 
        }, r => {
            if (r.success) {
                setTurns(r.data || []);
                const inProgress = r.data.find(t => t.TurnoEstado === 'LLAMADO');
                if (inProgress && !activeTurn) {
                    setActiveTurn(inProgress);
                    startTimer(inProgress);
                }
            }
            setLoading(false);
        });
    };

    const loadHistory = () => {
        request.Get(API_BASE + 'specialist/history', { specialistId }, r => {
            if (r.success) setHistory(r.data || []);
        });
    };

    useEffect(() => {
        loadData();
    }, [specialistId, activeSpecialtyId]);

    useEffect(() => {
        const interval = setInterval(loadQueue, 10000);
        return () => clearInterval(interval);
    }, [specialistId, activeSpecialtyId]);

    const startTimer = (turn) => {
        const duration = profile?.EspecialistaSlotDuracion || 20;
        setTimeLeft(duration * 60);
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
        }, 1000);
    };

    const handleStartAttention = (turn) => {
        request.Post(API_BASE + 'specialist/update-turn', { turnoId: turn.TurnoId, status: 'IN_PROGRESS' }, r => {
            if (r.success) {
                setActiveTurn(turn);
                startTimer(turn);
                loadQueue();
                Notify({ type: 'success', text: `Atención iniciada: ${turn.TurnoNombreCliente}` });
            }
        });
    };

    const handleFinishAttention = () => {
        setLoading(true);
        request.Post(API_BASE + 'specialist/update-turn', {
            turnoId: activeTurn.TurnoId,
            status: 'FINISHED',
            obs: observations
        }, r => {
            if (r.success) {
                clearInterval(timerRef.current);
                setActiveTurn(null);
                setObservations('');
                loadQueue();
                loadHistory();
                Notify({ type: 'success', text: 'Atención finalizada y correo enviado.' });
            }
            setLoading(false);
        });
    };

    const handleOpenCancelModal = (turn) => {
        setSelectedTurnForCancel(turn);
        setCancelReason('');
        setShowCancelModal(true);
    };

    const handleCancelTurn = () => {
        if (!cancelReason.trim()) {
            Notify({ type: 'info', text: 'Por favor, ingrese un motivo de cancelación.' });
            return;
        }

        setLoading(true);
        request.Post(API_BASE + 'specialist/cancel-turn', {
            turnoId: selectedTurnForCancel.TurnoId,
            reason: cancelReason
        }, r => {
            if (r.success) {
                setShowCancelModal(false);
                loadQueue();
                Notify({ type: 'success', text: 'Turno cancelado y cliente notificado.' });
            } else {
                Notify({ type: 'fail', text: 'Error al cancelar el turno.' });
            }
            setLoading(false);
        });
    };

    const handleExportCSV = () => {
        if (history.length === 0) {
            Notify({ type: 'info', text: 'No hay historial para exportar' });
            return;
        }
        let csv = "Fecha,Hora,Cliente,Especialidad,Estado,Observaciones\n";
        history.forEach(h => {
            csv += `${h.TurnoFch},${h.TurnoHra},${h.TurnoNombreCliente},${h.EspecialidadNombre},${h.TurnoEstado},${h.TurnoObservacion || ''}\n`;
        });
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `Historial_Especialista_${new Date().toLocaleDateString()}.csv`;
        link.click();
    };

    const getChartData = () => {
        return history.slice(0, 10).reverse().map(h => ({
            name: h.TurnoHra.substring(0, 5),
            atencion: 20,
            volumen: 1
        }));
    };

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s < 10 ? '0' : ''}${s}`;
    };

    return (
        <div className="turnero-container p-4 animate-in">
            {/* HEADER */}
            <header className="d-flex justify-content-between align-items-center mb-4">
                <div className="d-flex align-items-center">
                    <div className="user-avatar-premium mr-3">
                        <FontAwesomeIcon icon={faUserCircle} size="3x" className="text-primary opacity-80" />
                    </div>
                    <div>
                        <h2 className="turnero-title m-0">
                            {profile ? `${profile.EspecialistaApellido}, ${profile.EspecialistaNombre}` : 'Cargando Especialista...'}
                        </h2>
                        <p className="text-muted m-0">
                            <Badge bg="primary" className="mr-2">{profile?.SucursalNombre || 'Sucursal'}</Badge>
                            <span className="opacity-50 small">Consola de Especialista Premium</span>
                        </p>
                    </div>
                </div>
                <div className="d-flex align-items-center gap-4">
                    <div className="text-right mr-4 d-none d-md-block">
                        <div className="text-muted small font-weight-bold">HORA ACTUAL</div>
                        <div className="text-white h5 m-0 font-weight-bold">
                            {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                        </div>
                    </div>
                    <button className="turnero-btn turnero-btn-danger shadow-sm" onClick={onLogout}>
                        <FontAwesomeIcon icon={faSignOutAlt} className="mr-2" /> SALIR
                    </button>
                </div>
            </header>

            {/* SPECIALTY SELECTOR (CARDS) */}
            <div className="mb-4">
                <div className="d-flex flex-wrap gap-3">
                    {specialties.map(spec => (
                        <motion.div
                            key={spec.EspecialidadId}
                            whileHover={{ scale: 1.02 }}
                            className={`sector-card-premium ${activeSpecialtyId === spec.EspecialidadId ? 'active' : ''}`}
                            onClick={() => setActiveSpecialtyId(spec.EspecialidadId)}
                        >
                            <div className="sector-info">
                                <span className="sector-name">{spec.EspecialidadNombre}</span>
                                <span className="sector-acronym text-primary small">{spec.TipoNombre}</span>
                            </div>
                            <div className="sector-queue">
                                <FontAwesomeIcon icon={faTags} className="opacity-20 mr-2" />
                            </div>
                            {activeSpecialtyId === spec.EspecialidadId && <div className="active-glow"></div>}
                        </motion.div>
                    ))}
                </div>
            </div>

            <Row className="g-4">
                {/* LEFT: Queue & Tabs */}
                <Col lg={8}>
                    <div className="turnero-card-premium">
                        <Tabs defaultActiveKey="agenda" className="turnero-tabs-premium border-0">
                            <Tab eventKey="agenda" title={<span><FontAwesomeIcon icon={faHistory} className="mr-2" /> AGENDA</span>}>
                                <div className="turns-list-premium custom-scrollbar" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                    {loading && turns.length === 0 ? <div className="text-center p-5"><Spinner animation="border" variant="primary" /></div> : turns.length === 0 ? (
                                        <div className="p-5 text-center text-muted">No hay turnos pendientes para hoy.</div>
                                    ) : (
                                        <Table hover className="m-0 turnero-table">
                                            <thead>
                                                <tr>
                                                    <th>HORA</th>
                                                    <th>CLIENTE</th>
                                                    <th>ESTADO</th>
                                                    <th className="text-right">ACCIONES</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {turns.map(t => (
                                                    <tr 
                                                        key={t.TurnoId} 
                                                        className={`turn-row-premium ${activeTurn?.TurnoId === t.TurnoId ? 'active' : ''} ${t.TurnoEstado === 'FINALIZADO' ? 'completed' : ''}`}
                                                    >
                                                        <td className="font-weight-bold text-primary">{t.TurnoHra.substring(0, 5)}</td>
                                                        <td>
                                                            <div className="font-weight-bold">{t.TurnoNombreCliente || 'Cliente Web'}</div>
                                                            <div className="tiny text-muted">{t.TurnoEmailCliente}</div>
                                                        </td>
                                                        <td>
                                                            {t.TurnoValidado === 1 ? (
                                                                <Badge bg="success" className="validation-badge">VALIDADO</Badge>
                                                            ) : (
                                                                <Badge bg="secondary" className="validation-badge opacity-30">PENDIENTE</Badge>
                                                            )}
                                                        </td>
                                                        <td className="text-right">
                                                            <div className="d-flex justify-content-end gap-2">
                                                                {(!t.TurnoEstado || t.TurnoEstado === 'EN_COLA') && !activeTurn && (
                                                                    <>
                                                                        <button className="btn-action-premium" onClick={() => handleStartAttention(t)}>ATENDER</button>
                                                                        <button className="btn-action-premium-danger" onClick={() => handleOpenCancelModal(t)}>CANCELAR</button>
                                                                    </>
                                                                )}
                                                                {t.TurnoEstado === 'FINALIZADO' && <span className="text-muted small">COMPLETADO</span>}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    )}
                                </div>
                            </Tab>
                            <Tab eventKey="history" title={<span><FontAwesomeIcon icon={faHistory} className="mr-2" /> HISTORIAL</span>}>
                                <div className="p-0 table-responsive-premium">
                                    <Table hover className="m-0 turnero-table">
                                        <thead>
                                            <tr>
                                                <th>FECHA</th>
                                                <th>CLIENTE</th>
                                                <th>ESPECIALIDAD</th>
                                                <th>ESTADO</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {history.map(h => (
                                                <tr key={h.TurnoId}>
                                                    <td className="small">{h.TurnoFch} {h.TurnoHra.substring(0, 5)}</td>
                                                    <td className="font-weight-bold">{h.TurnoNombreCliente}</td>
                                                    <td className="small">{h.EspecialidadNombre}</td>
                                                    <td><Badge bg="info">{h.TurnoEstado}</Badge></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                    <div className="p-3 text-right">
                                        <button className="btn-premium-outline btn-sm" onClick={handleExportCSV}>
                                            <FontAwesomeIcon icon={faFileExcel} className="mr-2 text-success" /> EXPORTAR CSV
                                        </button>
                                    </div>
                                </div>
                            </Tab>
                            <Tab eventKey="stats" title={<span><FontAwesomeIcon icon={faChartLine} className="mr-2" /> ESTADÍSTICAS</span>}>
                                <div className="p-4">
                                    <h6 className="text-muted mb-4">ATENCIÓN ÚLTIMOS TURNOS</h6>
                                    <div style={{ width: '100%', height: 250 }}>
                                        <ResponsiveContainer>
                                            <AreaChart data={getChartData()}>
                                                <defs>
                                                    <linearGradient id="colorSpecialist" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="var(--turnero-primary)" stopOpacity={0.3} />
                                                        <stop offset="95%" stopColor="var(--turnero-primary)" stopOpacity={0} />
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                                                <XAxis dataKey="name" stroke="rgba(255,255,255,0.3)" fontSize={10} />
                                                <YAxis stroke="rgba(255,255,255,0.3)" fontSize={10} />
                                                <Tooltip contentStyle={{ background: '#1e293b', border: 'none' }} />
                                                <Area type="monotone" dataKey="atencion" stroke="var(--turnero-primary)" fillOpacity={1} fill="url(#colorSpecialist)" />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            </Tab>
                        </Tabs>
                    </div>
                </Col>

                {/* RIGHT: Active Panel */}
                <Col lg={4}>
                    <AnimatePresence mode="wait">
                        {activeTurn ? (
                            <motion.div 
                                key="active"
                                initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 20 }}
                                className="turnero-card-premium active-attention-panel p-4"
                            >
                                <div className="timer-section-premium text-center mb-4">
                                    <div className={`countdown-premium ${timeLeft < 60 ? 'warning-pulse' : ''}`}>
                                        {formatTime(timeLeft)}
                                    </div>
                                    <div className="tiny text-muted text-uppercase letter-spacing-1">TIEMPO RESTANTE</div>
                                    {timeLeft === 0 && <div className="time-up-tag">TIEMPO AGOTADO</div>}
                                </div>

                                <div className="client-header-premium mb-4">
                                    <h3 className="font-weight-bold mb-1">{activeTurn.TurnoNombreCliente}</h3>
                                    <div className="small text-muted">{activeTurn.TurnoEmailCliente}</div>
                                    <div className="small text-muted">DNI: {activeTurn.TurnoNCCliente}</div>
                                </div>

                                <div className="mb-4">
                                    <label className="tiny text-muted mb-2"><FontAwesomeIcon icon={faNotesMedical} className="mr-2" /> OBSERVACIONES</label>
                                    <textarea 
                                        className="turnero-input w-100" 
                                        style={{ height: '180px' }}
                                        placeholder="Ingrese notas de la atención..."
                                        value={observations}
                                        onChange={e => setObservations(e.target.value)}
                                    />
                                </div>

                                <button className="call-next-btn-premium w-100" onClick={handleFinishAttention} disabled={loading}>
                                    {loading ? <Spinner size="sm" animation="border" /> : (
                                        <><FontAwesomeIcon icon={faStop} className="mr-2" /> FINALIZAR ATENCIÓN</>
                                    )}
                                </button>
                            </motion.div>
                        ) : (
                            <div className="turnero-card-premium idle-panel p-5 text-center">
                                <FontAwesomeIcon icon={faUserClock} size="4x" className="text-primary opacity-10 mb-4" />
                                <h5 className="text-white">Panel de Espera</h5>
                                <p className="text-muted small">Selecciona un turno de la agenda para comenzar la atención controlada.</p>
                            </div>
                        )}
                    </AnimatePresence>
                </Col>
            </Row>

            {/* CANCELLATION MODAL */}
            <Modal
                show={showCancelModal}
                onHide={() => setShowCancelModal(false)}
                centered
                contentClassName="modal-finish-premium"
            >
                <Modal.Header closeButton className="modal-finish-header">
                    <Modal.Title className="h5 mb-0 text-white">
                        <FontAwesomeIcon icon={faTimesCircle} className="mr-2 text-danger" /> Cancelar Turno
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="modal-finish-body">
                    {selectedTurnForCancel && (
                        <div className="mb-4 p-3 bg-dark-glass rounded">
                            <div className="row">
                                <Col sm={6}>
                                    <label className="tiny text-muted">CLIENTE</label>
                                    <div className="font-weight-bold">{selectedTurnForCancel.TurnoNombreCliente}</div>
                                </Col>
                                <Col sm={6}>
                                    <label className="tiny text-muted">ID / DNI</label>
                                    <div className="font-weight-bold">{selectedTurnForCancel.TurnoNCCliente}</div>
                                </Col>
                                <Col sm={12} className="mt-2">
                                    <label className="tiny text-muted">EMAIL DE NOTIFICACIÓN</label>
                                    <div className="text-primary-light small">{selectedTurnForCancel.TurnoEmailCliente}</div>
                                </Col>
                            </div>
                        </div>
                    )}

                    <div className="form-group">
                        <label className="tiny text-muted mb-2">MOTIVO DE CANCELACIÓN (Se enviará al cliente)</label>
                        <textarea 
                            className="turnero-input w-100" 
                            style={{ height: '120px' }}
                            placeholder="Ej: El especialista ha tenido una emergencia, por favor reprograme su turno."
                            value={cancelReason}
                            onChange={e => setCancelReason(e.target.value)}
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer className="modal-finish-footer">
                    <button className="btn-premium-outline" onClick={() => setShowCancelModal(false)}>IGNORAR</button>
                    <button 
                        className="btn-premium-solid bg-danger border-danger" 
                        onClick={handleCancelTurn}
                        disabled={loading}
                    >
                        {loading ? <Spinner size="sm" animation="border" /> : 'CONFIRMAR CANCELACIÓN'}
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default SpecialistConsole;
